// Callback — exchanges the PKCE `code` for an in-memory access token, then routes onward.

import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Glyph } from "@/ui";
import { Alert, Button, CardContent, CardHeader } from "@/ui";
import { pageTitleClass } from "@/components/PageHeader";
import { Loading } from "@/components/States";
import { AuthLayout } from "@/components/AuthLayout";
import { useAuth } from "@/lib/auth";

export function Callback() {
  const { completeLogin } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const providerError = params.get("error");
    const code = params.get("code");
    const state = params.get("state");

    if (providerError) {
      const friendly: Record<string, string> = {
        // Documented prod boundary: Google OAuth identity exchange is a separate
        // Phase 1 task — outside dev mode, authorize bounces back with this code.
        login_provider_not_configured:
          "Login belum tersedia di server ini — penyedia identitas (Google) belum dikonfigurasi. Coba lagi nanti.",
        access_denied: "Login dibatalkan.",
      };
      setError(
        friendly[providerError] ??
          params.get("error_description") ??
          `Login ditolak (${providerError}).`,
      );
      return;
    }
    if (!code || !state) {
      setError("Parameter callback tidak lengkap. Silakan masuk ulang.");
      return;
    }

    completeLogin(code, state)
      .then((returnTo) => navigate(returnTo, { replace: true }))
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Gagal menyelesaikan login.");
      });
  }, [completeLogin, navigate, params]);

  return (
    <AuthLayout>
      {error ? (
        <>
          <CardHeader>
            <h1 className={pageTitleClass}>Login gagal</h1>
          </CardHeader>
          <CardContent className="gap-4">
            <Alert tone="danger" role="alert">
              {error}
            </Alert>
            <Button variant="primary" block onClick={() => navigate("/login", { replace: true })}>
              <Glyph name="arrowLeft" />
              Kembali ke halaman masuk
            </Button>
          </CardContent>
        </>
      ) : (
        <>
          <CardHeader>
            <h1 className={pageTitleClass}>Masuk ke akun</h1>
          </CardHeader>
          <Loading label="Menyelesaikan login…" />
        </>
      )}
    </AuthLayout>
  );
}
